import { useCallback, useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { IMoreOption, IRemisionPaymentInfo } from "src/types/molecules";
import { IPayRemision, IRemisionName } from "src/types/global";
import { remisionService } from "@services/RemisionService";
import { authService } from "@services/authService";
import { GlobalAlertContext } from "@providers/GlobalAlertProvider";
import RemisionPaymentInfoUI from "./RemisionPaymentInfoUI";
import { buildRemisionPrintModel } from "@components/molecules/RemisionPDFContent/remisionPrintModel";
const RemisionPaymentInfo = ({
  remision,
  setRemision
}: IRemisionPaymentInfo) => {
  const navigate = useNavigate();
  const remisionId = Number(remision.id);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Hubo un error");
  const [employeeCode, setEmployeeCode] = useState("");
  const [applicant, setApplicant] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(0);
  // SUGGESTION-C re-entrancy guard: a `ref`, not `isLoading` state — the
  // Popover already closes on click (MoreOptions.tsx), but a genuine
  // double-click can still deliver two click events before that state
  // update commits. A ref updates synchronously on the very first
  // invocation, so the second call sees it immediately, before either
  // await ever yields.
  const isPrintingRef = useRef(false);
  const {
    register,
    handleSubmit,
    control,
    reset: resetPay
  } = useForm<IPayRemision>();
  const {
    register: registerName,
    handleSubmit: handleName,
    setValue: setNameValue
  } = useForm<IRemisionName>();
  const {
    setGlobalAlertMessage,
    setGlobalAlertSeverity,
    showGlobalAlert,
    hiddenGlobalAlert
  } = useContext(GlobalAlertContext);
  const pay = useCallback(async (data: IPayRemision) => {
    try {
      setIsLoading(true);
      if (!employeeCode) throw new Error("Código de empleado requerido");
      hiddenGlobalAlert();
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("Remisión pagada");
      const payload: IPayRemision = {
        remisionId,
        employeeCode,
        paymentMethod,
        applicant: data.applicant
      };
      const res = await remisionService.pay(payload);
      if (!res.data.success) throw new Error(res.data.message);
      setRemision({
        ...remision,
        state: "Pagado",
        remainingAmount: 0
      });
      showGlobalAlert();
      setShowPayDialog(false);
      setEmployeeCode("");
      setApplicant("");
      setPaymentMethod(0);
      resetPay();
    } catch (error: any) {
      if (error?.message?.includes("zero") || error?.message?.includes("Zero")) {
        setErrorMessage("Error, la remisión no tiene productos con valor");
      } else {
        setErrorMessage("Lo sentimos, no se pudo registrar el pago");
      }
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [employeeCode, paymentMethod, remisionId, remision, setRemision, resetPay, hiddenGlobalAlert, setGlobalAlertSeverity, setGlobalAlertMessage, showGlobalAlert]);
  const editName = useCallback(async (data: IRemisionName) => {
    try {
      setIsLoading(true);
      data.remisionId = remisionId;
      await remisionService.putName(data);
      setRemision({
        ...remision,
        name: data.name.toLocaleLowerCase()
      });
    } catch {
      setErrorMessage("Error, inténtalo de nuevo");
      setShowError(true);
    } finally {
      setShowNameDialog(false);
      setIsLoading(false);
    }
  }, [remisionId, remision, setRemision]);
  const deleteRemision = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!employeeCode) throw new Error();
      hiddenGlobalAlert();
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("¡Listo! La remisión ha sido eliminada correctamente.");
      await remisionService.remove(remisionId, employeeCode);
      navigate("/remisiones", {
        state: {
          showAlert: true
        }
      });
      showGlobalAlert();
    } catch {
      setErrorMessage("Error, intentalo de nuevo");
      setShowError(true);
    } finally {
      setIsLoading(false);
      setEmployeeCode("");
    }
  }, [employeeCode, remisionId, navigate, hiddenGlobalAlert, setGlobalAlertSeverity, setGlobalAlertMessage, showGlobalAlert]);

  /**
   * Fetches fresh remision data (never the page's already-formatted local
   * state — see RemisionDetail.tsx:79-80/:104), builds the print model, and
   * opens the generated PDF in a new tab. `@react-pdf/renderer` and
   * RemisionPDFContent are dynamically imported INSIDE the handler, never at
   * module top level — a top-level import would drag pdfkit into this
   * component's module graph, which RemisionDetail.test.tsx's existing green
   * suite reaches via RemisionDetailUI.tsx (craco.config.js only whitelists
   * axios for transformation). No state gate: printing a Creado remision
   * (blank FORMA DE PAGO) is legitimate.
   */
  const printRemision = useCallback(async () => {
    // SUGGESTION-C: re-entrancy guard. The Popover now closes on click
    // (MoreOptions.tsx), but a genuine double-click can still deliver two
    // click events before that state update commits, firing two fetches,
    // two renders and two window.open calls (two tabs).
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;
    try {
      const {
        data
      } = await remisionService.get(remisionId);
      const [{
        pdf
      }, {
        default: RemisionPDFContent
      }] = await Promise.all([import("@react-pdf/renderer"), import("@components/molecules/RemisionPDFContent/RemisionPDFContent")]);
      const model = buildRemisionPrintModel(data);
      const blob = await pdf(RemisionPDFContent(model)).toBlob();
      const url = URL.createObjectURL(blob);
      const printTab = window.open(url, "_blank");

      // SUGGESTION-D: a popup blocker makes window.open return null/undefined
      // silently — the one failure mode the try/catch below never sees
      // (nothing throws). Route it through the same user-visible error path
      // as fetch/render failures instead of failing silently.
      if (!printTab) {
        URL.revokeObjectURL(url);
        setErrorMessage("El navegador bloqueó la ventana del documento. Permite las ventanas emergentes e inténtalo de nuevo.");
        setShowError(true);
        return;
      }

      // Revoke after the new tab has had a chance to load the blob, not
      // synchronously — revoking right away can invalidate the blob before
      // the browser finishes loading it in the new tab.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setErrorMessage("No se pudo generar el documento para imprimir");
      setShowError(true);
    } finally {
      isPrintingRef.current = false;
    }
  }, [remisionId]);
  const options: IMoreOption[] = [{
    show: remision.state === "Creado" && remision.receiptType !== 1,
    primary: "Agregar productos",
    path: `/remisiones/${remision.id}/productos`,
    state: {
      ...remision,
      requestProducts: [],
      returnProducts: []
    }
  }, {
    show: true,
    primary: "Eliminar",
    onClick: () => setShowDeleteDialog(true)
  }, {
    show: true,
    primary: "Editar nombre",
    onClick: () => setShowNameDialog(true)
  }, {
    show: true,
    primary: "Imprimir",
    onClick: () => printRemision()
  }];
  const handleClosePayDialog = () => {
    setShowPayDialog(false);
    setEmployeeCode("");
    setApplicant("");
    setPaymentMethod(0);
    resetPay();
  };
  const handleCloseNameDialog = () => {
    setShowNameDialog(false);
    setNameValue("name", "");
    setNameValue("employeeCode", "");
  };
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setEmployeeCode("");
  };
  return <RemisionPaymentInfoUI remisionId={remision.id} state={remision.state} total={remision.total} remainingAmount={remision.remainingAmount} showError={showError} closeError={() => setShowError(false)} errorMessage={errorMessage} showPayDialog={showPayDialog} setShowPayDialog={setShowPayDialog} showDeleteDialog={showDeleteDialog} setShowDeleteDialog={setShowDeleteDialog} showNameDialog={showNameDialog} setShowNameDialog={setShowNameDialog} register={register} control={control} pay={handleSubmit(pay)} registerName={registerName} editName={handleName(editName)} isLoading={isLoading} moreOptions={options} deleteRemision={deleteRemision} isAdmin={authService.isAdmin()} employeeCode={employeeCode} setEmployeeCode={setEmployeeCode} applicant={applicant} setApplicant={setApplicant} handleClosePayDialog={handleClosePayDialog} handleCloseNameDialog={handleCloseNameDialog} handleCloseDeleteDialog={handleCloseDeleteDialog} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} onViewStateHistory={() => navigate(`/remisiones/${remisionId}/estados`)} />;
};
export default RemisionPaymentInfo;